import { organizationServiceApi } from '@/shared/api/organization-service.api';
import { AppStoreTag } from '@/shared/constants/app-store-tag.constant';
import { PersonOption } from '@/shared/lib/interfaces/person-option.interface';

import { transformPowerOfAttorneyUser } from '../model/helpers/transform-power-of-attorney-user.helper';
import {
	PowerOfAttorneyCandidatesResponse,
	PowerOfAttorneyDelegate,
	PowerOfAttorneyDelegatesResponse,
	RemovePowerOfAttorneyDelegateVariables,
	UpdatePowerOfAttorneyDelegatesVariables,
} from '../model/types/power-of-attorney-api.interface';

const powerOfAttorneyApi = organizationServiceApi.injectEndpoints({
	endpoints: (build) => ({
		getPowerOfAttorney: build.query<PowerOfAttorneyDelegate[], string>({
			providesTags: [AppStoreTag.PowerOfAttorney],
			query: (boatUuid) => ({
				method: 'GET',
				url: `organization/yachts/${boatUuid}/data-sharing/delegates`,
			}),
			transformResponse: (response: PowerOfAttorneyDelegatesResponse): PowerOfAttorneyDelegate[] =>
				(response.items ?? []).map(transformPowerOfAttorneyUser),
		}),
		getPowerOfAttorneyCandidates: build.query<PersonOption[], string>({
			providesTags: [AppStoreTag.PowerOfAttorneyCandidates],
			query: (boatUuid) => ({
				method: 'GET',
				url: `organization/yachts/${boatUuid}/data-sharing/delegates/candidates`,
			}),
			transformResponse: (response: PowerOfAttorneyCandidatesResponse): PersonOption[] =>
				(response.items ?? []).map(transformPowerOfAttorneyUser).map(({ id, name, email, initials }) => ({
					value: id,
					label: name,
					email,
					initials,
				})),
		}),
		updatePowerOfAttorneyDelegates: build.mutation<PowerOfAttorneyDelegate[], UpdatePowerOfAttorneyDelegatesVariables>({
			invalidatesTags: [AppStoreTag.PowerOfAttorneyCandidates],
			query: ({ boatUuid, userIds }) => ({
				method: 'PUT',
				url: `organization/yachts/${boatUuid}/data-sharing/delegates`,
				body: { delegate_user_ids: userIds },
			}),
			transformResponse: (response: PowerOfAttorneyDelegatesResponse): PowerOfAttorneyDelegate[] =>
				(response.items ?? []).map(transformPowerOfAttorneyUser),
			async onQueryStarted({ boatUuid }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(powerOfAttorneyApi.util.upsertQueryData('getPowerOfAttorney', boatUuid, data));
				} catch {}
			},
		}),
		removePowerOfAttorneyDelegate: build.mutation<PowerOfAttorneyDelegate[], RemovePowerOfAttorneyDelegateVariables>({
			invalidatesTags: [AppStoreTag.PowerOfAttorneyCandidates],
			query: ({ boatUuid, delegateId }) => ({
				method: 'DELETE',
				url: `organization/yachts/${boatUuid}/data-sharing/delegates/${delegateId}`,
			}),
			transformResponse: (response: PowerOfAttorneyDelegatesResponse): PowerOfAttorneyDelegate[] =>
				(response.items ?? []).map(transformPowerOfAttorneyUser),
			async onQueryStarted({ boatUuid }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(powerOfAttorneyApi.util.upsertQueryData('getPowerOfAttorney', boatUuid, data));
				} catch {}
			},
		}),
	}),
});

export const {
	useGetPowerOfAttorneyQuery,
	useGetPowerOfAttorneyCandidatesQuery,
	useUpdatePowerOfAttorneyDelegatesMutation,
	useRemovePowerOfAttorneyDelegateMutation,
} = powerOfAttorneyApi;
